import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { logger } from '../../utils/logger';

type ModalContextType = {
  isModalOpen: boolean;
  activeModalId: string | null;
  registerModal: (id: string) => void;
  unregisterModal: (id: string) => void;
};

const ModalContext = createContext<ModalContextType>({
  isModalOpen: false,
  activeModalId: null,
  registerModal: () => {},
  unregisterModal: () => {},
});

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [registeredModals, setRegisteredModals] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModalId, setActiveModalId] = useState<string | null>(null);

  // Update modal state whenever the registered modals change
  useEffect(() => {
    const hasModals = registeredModals.length > 0;
    setIsModalOpen(hasModals);
    setActiveModalId(hasModals ? registeredModals[registeredModals.length - 1] : null);
  }, [registeredModals]);

  const registerModal = useCallback((id: string) => {
    logger.info('[ModalContext] Registering modal with ID:', id);
    // Only add if not already registered
    setRegisteredModals(prev => {
      if (prev.includes(id)) {
        return prev;
      }
      return [...prev, id];
    });
  }, []);

  const unregisterModal = useCallback((id: string) => {
    logger.info('[ModalContext] Unregistering modal with ID:', id);
    setRegisteredModals(prev => prev.filter(modalId => modalId !== id));
  }, []);

  return (
    <ModalContext.Provider value={{ 
      isModalOpen, 
      activeModalId, 
      registerModal, 
      unregisterModal 
    }}>
      {children}
    </ModalContext.Provider>
  );
};

export const useModalContext = () => useContext(ModalContext);
